import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import type { ConvertPricesRequest, UpdatePricesBatchRequest } from "../productsApi";
import { convertPrices, updatePricesBatch } from "../productsApi";
import type { PriceType } from "../priceType";
import { isProductSellingPricesQuery } from "../useProductSellingPrices";

export const useOptionPricesBatch = (productId: string) => {
  const queryClient = useQueryClient();

  const updatePricesMutation = useMutation<
    { productId: string; optionIds: string[] },
    unknown,
    { priceType: PriceType; request: UpdatePricesBatchRequest }
  >({
    mutationFn: ({ priceType, request }) => updatePricesBatch(productId, priceType, request),
    onSuccess: async ({ optionIds }) => {
      enqueueSnackbar("Cenniki zostały zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
      await Promise.all(
        optionIds.map((oId) =>
          queryClient.invalidateQueries({ queryKey: ["products", productId, "options", oId, "prices"] }),
        ),
      );
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować cenników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const convertPricesMutation = useMutation<{ productId: string; optionIds: string[] }, unknown, ConvertPricesRequest>({
    mutationFn: (r) => convertPrices(productId, r),
    onSuccess: async ({ optionIds }) => {
      enqueueSnackbar("Cenniki zostały zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
      await Promise.all(
        optionIds.map((oId) =>
          queryClient.invalidateQueries({ queryKey: ["products", productId, "options", oId, "prices"] }),
        ),
      );
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować cenników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    updatePrices: async (priceType: PriceType, request: UpdatePricesBatchRequest) => {
      try {
        await updatePricesMutation.mutateAsync({ priceType, request });
      } catch {
        // Failure is already reported to the user by the mutation's onError
      }
    },
    convertPrices: async (request: ConvertPricesRequest) => {
      try {
        await convertPricesMutation.mutateAsync(request);
      } catch {
        // Failure is already reported to the user by the mutation's onError
      }
    },
  };
};
